import React, { useState, useEffect } from "react";
import { FaPrint, FaWhatsapp, FaCamera, FaTimes } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa";
import {
  FiUpload,
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiCalendar,
  FiMessageSquare,
} from "react-icons/fi";
import axios from "axios";
import { API_URL } from "../../../config";

const VisitorForm = () => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [visitPurpose, setVisitPurpose] = useState([]);
  const [visitorId, setVisitorId] = useState("");
  const [formData, setFormData] = useState({
    visitorID: "",
    name: "",
    mobileNumber: "",
    whatsappNumber: "",
    category: "",
    email: "",
    address: "",
    purpose: "",
    toMeet: "",
    query: "",
    salutation: "",
    additionalName: "",
    dob: "",
    whatsappAvailable: false,
    assembly: "",
    district: "",
    state: "",
    DocumentImage: [],
    igrsNumber: "",
  });

  const [errors, setErrors] = useState({});
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [districtSearch, setDistrictSearch] = useState("");
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [pdfName, setPdfName] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date().toLocaleString());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle file upload
  const handleCapture = (event) => {
    const files = Array.from(event.target.files);

    if (files.length > 1 || formData.DocumentImage.length + files.length > 1) {
      alert("You can upload only 1 file.");
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];

    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      alert("Only images (JPG, PNG) and PDFs are allowed.");
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      DocumentImage: [...validFiles],
    }));

    validFiles.forEach((file) => {
      if (file.type.startsWith("image")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews([reader.result]);
          setPdfName("");
        };
        reader.readAsDataURL(file);
      } else if (file.type === "application/pdf") {
        setPdfName(file.name);
        setPreviews([]);
      }
    });

    event.target.value = null;
  };

  const handleRemoveFile = () => {
    setPreviews([]);
    setPdfName("");
    setFormData((prevData) => ({
      ...prevData,
      DocumentImage: [],
    }));
  };

  // Print visitor details
  const handlePrint = () => {
    window.print();
  };

  // Fetch categories and visitor ID from the server
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const response = await axios.get(`${API_URL}getCategoriesList`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === "success") {
        setCategories(response.data.data);
        setVisitorId(response.data.visitor_id);
        setVisitPurpose(response.data.visitPurpose || []);

        setFormData((prev) => ({
          ...prev,
          visitorID: response.data.visitor_id,
          purpose: "",
        }));
      } else {
        console.error("Failed to fetch categories:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Save visitor details to the server
  const handleSave = async () => {
    // Validation for location fields
    if (!formData.state || !formData.district || !formData.assembly) {
      setErrors(prev => ({
        ...prev,
        state: !formData.state ? ["State is required"] : [],
        district: !formData.district ? ["District is required"] : [],
        assembly: !formData.assembly ? ["Assembly is required"] : []
      }));
      alert("Please fill in all location fields (State, District, and Assembly).");
      return;
    }

    setLoading(true);

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Authorization token is missing.");
      setLoading(false);
      return;
    }

    const payload = new FormData();
    payload.append("visitor_id", formData.visitorID);
    payload.append("visitor_name", formData.name);
    payload.append("mobile_number", formData.mobileNumber);
    payload.append("whatsappNumber", formData.whatsappNumber);
    payload.append("categorie_name", formData.category || "Politically Active");
    payload.append("email_id", formData.email);
    payload.append("address", formData.address);
    payload.append("visit_purpose", formData.purpose || "Problem");
    payload.append("to_meet", formData.toMeet);
    payload.append("query", formData.query);
    payload.append("salutation", formData.salutation);
    payload.append("category_text", formData.additionalName);
    payload.append("dob", formData.dob);
    payload.append("whatsapp_tick", formData.whatsappAvailable ? "1" : "0");
    payload.append("assembly", formData.assembly);
    payload.append("district", formData.district);
    payload.append("state", formData.state);
    payload.append("igrs_number", formData.igrsNumber);

    try {
      const response = await axios.post(
        `${API_URL}createVisitordetails`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        setSuccessData(response.data);
        setShowSuccessAlert(true);
        
        // Reset form
        setFormData({
          visitorID: visitorId,
          name: "",
          mobileNumber: "",
          category: "",
          email: "",
          address: "",
          purpose: "",
          toMeet: "",
          query: "",
          salutation: "",
          additionalName: "",
          dob: "",
          DocumentImage: [],
          assembly: "",
          district: "",
          igrsNumber: "",
          whatsappNumber: "",
          state: "",
          whatsappAvailable: false,
        });
        setPreviews([]);
        setPdfName("");
        setErrors({});
        await fetchCategories();
        
        // Auto-hide success alert after 10 seconds
        setTimeout(() => {
          setShowSuccessAlert(false);
          setSuccessData(null);
        }, 30000);
      } else {
        alert(response.data.message || "Failed to save visitor details.");
      }
    } catch (error) {
      if (error.response) {
        alert(error.response.data.message || "An unexpected error occurred.");
        if (error.response.data.errors) {
          setErrors(error.response.data.errors);
        }
      } else {
        console.error("An unexpected error occurred:", error);
        alert("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch locations
  useEffect(() => {
    axios
      .post(`${API_URL}locations`, { state_name: null, dist_name1: null })
      .then((response) => {
        if (response.data.success) {
          setStates(response.data.states);
          setFilteredStates(response.data.states);

          const defaultState =
            response.data.states[22] || response.data.states[0];
          setSelectedState(defaultState);

          setFormData((prevFormData) => ({
            ...prevFormData,
            state: defaultState,
          }));

          axios
            .post(`${API_URL}locations`, { state_name: defaultState })
            .then((districtResponse) => {
              setDistricts(districtResponse.data.districts || []);
              setFilteredDistricts(districtResponse.data.districts || []);
            })
            .catch((error) =>
              console.error("Error fetching districts:", error)
            );
        }
      })
      .catch((error) => console.error("Error fetching locations:", error));
  }, []);

  // Update districts when state is selected
  const handleStateChange = (state) => {
    setSelectedState(state);
    setSelectedDistrict("");
    setAssemblies([]);
    setDistricts([]);

    setFormData((prev) => ({ ...prev, state, district: "", assembly: "" }));

    axios
      .post(`${API_URL}locations`, { state_name: state })
      .then((response) => {
        setDistricts(response.data.districts || []);
        setFilteredDistricts(response.data.districts || []);
      })
      .catch((error) => console.error("Error fetching districts:", error));
  };

  const handleDistrictChange = (district) => {
    setSelectedDistrict(district);
    setFormData({ ...formData, district, assembly: "" });

    axios
      .post(`${API_URL}locations`, {
        state_name: formData.state,
        dist_name1: district,
      })
      .then((response) => {
        setAssemblies(response.data.assemblies || []);
      })
      .catch((error) => console.error("Error fetching assemblies:", error));
  };

  // Handle state search
  const handleStateSearch = (searchTerm) => {
    setStateSearch(searchTerm);
    setFilteredStates(
      states.filter((state) =>
        state.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  // Handle district search
  const handleDistrictSearch = (searchTerm) => {
    setDistrictSearch(searchTerm);
    setFilteredDistricts(
      districts.filter((district) =>
        district.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-indigo-50 p-4 font-sans">
      {/* Success Alert Modal */}
      {showSuccessAlert && successData && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Registration Successful!</h3>
                    <p className="text-green-100">Visitor details have been saved successfully</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowSuccessAlert(false);
                    setSuccessData(null);
                  }}
                  className="text-white hover:text-green-100 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3 text-green-700">
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="font-medium">{successData.message}</p>
                </div>
              </div>

              {/* Visitor Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Visitor Information</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Visitor ID:</span>
                      <span className="font-semibold text-green-700">{successData.data.visitor_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-semibold text-gray-800">{successData.data.visitor_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mobile Number:</span>
                      <span className="font-semibold text-gray-800">{successData.data.mobile_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tracking ID:</span>
                      <span className="font-bold text-blue-600">{successData.data.traking_id}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Location Details</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">State:</span>
                      <span className="font-semibold text-gray-800">{successData.data.state}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">District:</span>
                      <span className="font-semibold text-gray-800">{successData.data.district}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Assembly:</span>
                      <span className="font-semibold text-gray-800">{successData.data.assembly}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowSuccessAlert(false);
                    setSuccessData(null);
                  }}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Continue
                </button>
                <button
                  onClick={handlePrint}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <FaPrint className="w-5 h-5" />
                  Print Details
                </button>
                <button
                  onClick={() => {
                    // Copy visitor details to clipboard
                    const details = `Visitor ID: ${successData.data.visitor_id}\nName: ${successData.data.visitor_name}\nMobile: ${successData.data.mobile_number}\nTracking ID: ${successData.data.traking_id}`;
                    navigator.clipboard.writeText(details);
                    alert("Visitor details copied to clipboard!");
                  }}
                  className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Details
                </button>
              </div>

              {/* Auto-close timer */}
              <div className="mt-4">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full animate-progress"
                    style={{ animationDuration: '30s' }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 text-center mt-2">
                  This message will auto-close in 30 seconds
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-green-600 to-indigo-700 p-6 text-white">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Visitor Registration</h1>
              <p className="text-green-100 flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {currentDateTime}
              </p>
            </div>
            <button
              onClick={handlePrint}
              className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2 bg-white text-green-700 rounded-lg transition-all duration-200 hover:bg-green-50 hover:shadow-md"
            >
              <FaPrint />
              <span className="font-medium">Print</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-green-600 rounded-full"></div>
              <h2 className="text-xl font-semibold text-gray-800">
                Personal Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
              {/* Salutation & Name */}
              <div className="md:col-span-9 grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <label
                    htmlFor="salutation"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Salutation
                  </label>
                  <div className="relative">
                    <select
                      name="salutation"
                      id="salutation"
                      value={formData.salutation}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none"
                    >
                      <option value="">Select</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Prof.">Prof.</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg
                        className="fill-current h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-9">
                  <label
                    htmlFor="name"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Full Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter visitor name"
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  {errors.visitor_name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.visitor_name[0]}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
              {/* Mobile Number */}
              <div className="md:col-span-4">
                <label
                  htmlFor="mobileNumber"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Mobile Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiPhone className="text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="mobileNumber"
                    id="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        handleInputChange({
                          target: { name: "mobileNumber", value },
                        });
                        if (formData.whatsappAvailable) {
                          handleInputChange({
                            target: { name: "whatsappNumber", value },
                          });
                        }
                      }
                    }}
                    placeholder="Enter mobile number"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                {formData.mobileNumber?.length > 0 &&
                  formData.mobileNumber.length !== 10 && (
                    <p className="text-red-500 text-sm mt-1">
                      Mobile number must be 10 digits long.
                    </p>
                  )}
                {errors.mobile_number && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.mobile_number[0]}
                  </p>
                )}
              </div>

              {/* WhatsApp */}
              <div className="md:col-span-4">
                <label className="block text-gray-700 font-medium mb-2">
                  WhatsApp
                </label>
                <div className="flex items-center mt-1">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id="whatsappAvailable"
                      checked={formData.whatsappAvailable}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleInputChange({
                          target: {
                            name: "whatsappAvailable",
                            value: checked,
                          },
                        });
                        if (checked) {
                          handleInputChange({
                            target: {
                              name: "whatsappNumber",
                              value: formData.mobileNumber,
                            },
                          });
                        } else {
                          handleInputChange({
                            target: { name: "whatsappNumber", value: "" },
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-gray-700 flex items-center">
                      <FaWhatsapp className="text-green-500 text-xl mr-1" />{" "}
                      Same as mobile
                    </span>
                  </label>
                </div>
              </div>

              {/* WhatsApp Number (if different) */}
              {!formData.whatsappAvailable && (
                <div className="md:col-span-4">
                  <label
                    htmlFor="whatsappNumber"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    WhatsApp Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaWhatsapp className="text-green-500" />
                    </div>
                    <input
                      type="tel"
                      name="whatsappNumber"
                      id="whatsappNumber"
                      value={formData.whatsappNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) {
                          handleInputChange({
                            target: { name: "whatsappNumber", value },
                          });
                        }
                      }}
                      placeholder="Enter WhatsApp number"
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  {formData.whatsappNumber.length > 0 &&
                    formData.whatsappNumber.length !== 10 && (
                      <p className="text-red-500 text-sm mt-1">
                        WhatsApp number must be 10 digits long.
                      </p>
                    )}
                  {errors.whatsapp_number && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.whatsapp_number[0]}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
              {/* Category */}
              <div className="md:col-span-4">
                <label
                  htmlFor="category"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Category
                </label>
                <div className="relative">
                  <select
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {errors.categorie_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.categorie_name[0]}
                  </p>
                )}
              </div>

              {/* Additional Name for VIP/Other */}
              {(formData.category === "Others" ||
                formData.category === "VIP") && (
                <div className="md:col-span-4">
                  <label
                    htmlFor="additionalName"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Specify{" "}
                    {formData.category === "VIP" ? "VIP Details" : "Category"}
                  </label>
                  <input
                    type="text"
                    id="additionalName"
                    name="additionalName"
                    value={formData.additionalName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder={
                      formData.category === "VIP"
                        ? "VIP designation"
                        : "Specify category"
                    }
                  />
                </div>
              )}

              {/* Email */}
              <div className="md:col-span-4">
                <label
                  htmlFor="email"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiMail className="text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                {formData.email?.length > 0 && (
                  <p className="text-red-500 text-sm mt-1">
                    {formData.email.length < 5
                      ? "Email must be at least 5 characters long"
                      : ""}
                    {formData.email &&
                      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                        formData.email
                      ) &&
                      "Email must be a valid format"}
                  </p>
                )}
                {errors.email_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.email_id[0]}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
              {/* Address */}
              <div className="md:col-span-6">
                <label
                  htmlFor="address"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiMapPin className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="address"
                    id="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter complete address"
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                {errors.address && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.address[0]}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div className="md:col-span-3">
                <label
                  htmlFor="dob"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Date of Birth
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiCalendar className="text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="dob"
                    id="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                {errors.dob && (
                  <p className="text-red-500 text-sm mt-1">{errors.dob[0]}</p>
                )}
              </div>

              {/* To Meet */}
              <div className="md:col-span-3">
                <label
                  htmlFor="toMeet"
                  className="block text-gray-700 font-medium mb-2"
                >
                  To Meet
                </label>
                <input
                  type="text"
                  name="toMeet"
                  id="toMeet"
                  value={formData.toMeet}
                  onChange={handleInputChange}
                  placeholder="Person to meet"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                {errors.to_meet && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.to_meet[0]}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-green-600 rounded-full"></div>
              <h2 className="text-xl font-semibold text-gray-800">
                Visit Details
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
              {/* Purpose */}
              <div className="md:col-span-4">
                <label
                  htmlFor="purpose"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Purpose of Visit
                </label>
                <div className="relative">
                  <select
                    name="purpose"
                    id="visitPurpose"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none"
                  >
                    <option value="">Select Purpose</option>
                    {visitPurpose.map((purpose) => (
                      <option
                        key={purpose.visit_purpose}
                        value={purpose.visit_purpose}
                      >
                        {purpose.visit_purpose}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {errors.visit_purpose && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.visit_purpose[0]}
                  </p>
                )}
              </div>

              {/* IGRS Number */}
              <div className="md:col-span-4">
                <label
                  htmlFor="igrsNumber"
                  className="block text-gray-700 font-medium mb-2"
                >
                  IGRS/Jansunwai Number
                </label>
                <input
                  type="text"
                  name="igrsNumber"
                  id="igrsNumber"
                  value={formData.igrsNumber}
                  onChange={handleInputChange}
                  placeholder="Enter reference number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                {errors.igrsNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.igrsNumber[0]}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-8 bg-green-600 rounded-full"></div>
              <h2 className="text-xl font-semibold text-gray-800">
                Location Information <span className="text-red-500">*</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
              {/* State - Compulsory */}
              <div className="md:col-span-4">
                <label className="block text-gray-700 font-medium mb-3">
                  State <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    className={`w-full p-3 border ${
                      errors.state ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white font-medium`}
                    value={formData.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                  >
                    <option value="">Select State</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    {filteredStates
                      .filter((state) => state !== "Uttar Pradesh")
                      .map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
                    <svg
                      className="fill-current h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {errors.state && (
                  <p className="text-red-500 text-sm mt-1">{errors.state[0]}</p>
                )}
              </div>

              {/* District - Compulsory */}
              <div className="md:col-span-4">
                <label className="block text-gray-700 font-medium mb-3">
                  District <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <select
                    className={`w-full p-3 border ${
                      errors.district ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white transition-all duration-200 group-hover:border-green-400`}
                    value={formData.district}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    disabled={!selectedState}
                  >
                    <option value="">
                      Select District {selectedState ? `in ${selectedState}` : ""}
                    </option>
                    {filteredDistricts.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
                    <svg
                      className="fill-current h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {!selectedState && (
                  <div className="flex items-center mt-2 text-sm text-amber-600">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Select a state to choose district
                  </div>
                )}
                {errors.district && (
                  <p className="text-red-500 text-sm mt-1">{errors.district[0]}</p>
                )}
              </div>

              {/* Assembly - Compulsory */}
              <div className="md:col-span-4">
                <label className="block text-gray-700 font-medium mb-3">
                  Assembly <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <select
                    className={`w-full p-3 border ${
                      errors.assembly ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white transition-all duration-200 group-hover:border-green-400`}
                    value={formData.assembly}
                    onChange={(e) =>
                      setFormData({ ...formData, assembly: e.target.value })
                    }
                    disabled={!selectedDistrict}
                  >
                    <option value="">
                      Select Assembly {selectedDistrict ? `in ${selectedDistrict}` : ""}
                    </option>
                    {assemblies.map((assembly) => (
                      <option key={assembly} value={assembly}>
                        {assembly}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
                    <svg
                      className="fill-current h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {!selectedDistrict && (
                  <div className="flex items-center mt-2 text-sm text-amber-600">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Select a district to choose assembly
                  </div>
                )}
                {errors.assembly && (
                  <p className="text-red-500 text-sm mt-1">{errors.assembly[0]}</p>
                )}
              </div>
            </div>

            {/* Required Fields Indicator */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-blue-800 font-medium">All location fields are required</h3>
                  <p className="text-blue-600 text-sm mt-1">
                    Please select State, District, and Assembly to complete the registration.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-green-600 rounded-full"></div>
              <h2 className="text-xl font-semibold text-gray-800">
                Document Upload
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
              <div className="md:col-span-6">
                <label
                  htmlFor="document"
                  className="block text-gray-700 font-medium mb-2"
                >
                  Upload Document (Image or PDF)
                </label>

                <div className="flex items-center gap-4">
                  <label
                    htmlFor="document"
                    className="flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors duration-200 border-2 border-dashed border-gray-300 hover:border-green-400"
                  >
                    <FiUpload className="text-green-600" />
                    <span>Choose File</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    capture="environment"
                    id="document"
                    onChange={handleCapture}
                    className="hidden"
                  />

                  {previews.length > 0 || pdfName ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600 bg-green-50 px-3 py-2 rounded-lg">
                      <span className="text-green-700 font-medium">
                        {pdfName || "Image selected"}
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 italic">
                      No file chosen
                    </span>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Supported formats: JPG, PNG, PDF • Maximum 1 file • Optional
                  field
                </p>

                {/* Preview */}
                {previews.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 mb-2 font-medium">
                      Document Preview:
                    </p>
                    <div className="flex items-center gap-4">
                      <img
                        src={previews[0]}
                        alt="Document preview"
                        className="w-32 h-32 object-contain border rounded-lg shadow-sm"
                      />
                      <div className="text-sm text-gray-600">
                        <p>✓ Document ready for upload</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Click the remove button to delete
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {pdfName && !previews.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 mb-2 font-medium">
                      PDF Document:
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-32 h-32 bg-red-100 rounded-lg flex items-center justify-center">
                        <FaFilePdf className="text-red-500 text-3xl" />
                      </div>
                      <div className="text-sm text-gray-600">
                        <p className="font-medium text-green-700">{pdfName}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF document ready for upload
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {errors.document_image && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.document_image[0]}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-8 bg-green-600 rounded-full"></div>
              <h2 className="text-xl font-semibold text-gray-800">Query</h2>
            </div>

            <div className="mb-6">
              <label
                htmlFor="query"
                className="block text-gray-700 font-medium mb-2"
              >
                Type Your Query Here
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 pt-3 pointer-events-none">
                  <FiMessageSquare className="text-gray-400" />
                </div>
                <textarea
                  name="query"
                  id="query"
                  value={formData.query}
                  onChange={handleInputChange}
                  placeholder="Please describe your query in detail..."
                  className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows="4"
                ></textarea>
              </div>
              {errors.query && (
                <p className="text-red-500 text-sm mt-1">{errors.query[0]}</p>
              )}
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className={`px-8 py-3 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Save Visitor Details"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorForm;